import { Button, Popconfirm, Popover } from 'antd';
import React, { useMemo } from 'react';

import Icon from '@ant-design/icons';
import ButtonKit from '@components/common/ButtonKit';
import {
  StyledAntAlert,
  ToolBarContainer,
  ToolBarLeft,
  ToolBarRight,
} from '@components/detail-panel/components/ToolBar/styled';
import { IToolBar } from '@components/detail-panel/components/ToolBar/typed';
import AlertWarningIcon from '@icons/event-management/alert-warning.svg';
import CloseBlueIcon from '@icons/event-management/close-blue.svg';
import DeleteBlueIcon from '@icons/event-management/delete-blue.svg';
import EditIcon from '@icons/event-management/edit.svg';
import SaveIcon from '@icons/event-management/save.svg';
import UnblockIcon from '@icons/event-management/unblock.svg';
import CloneIcon from '@icons/emp/clone.svg';
import { EEventManagementTabsType } from '@src/common/constant/enum';
import { MappedEvent } from '@src/emp-3.0/types/mapped-event';
import { EDraftStatus } from '@src/emp-3.0/types';
import StatusTag from '@src/emp-3.0/components/common/StatusTag';
import { useCreatePermissions } from '@src/hooks/usePermissions';
import {
  hasRelatedFlows,
  hasRelatedMetrics,
  isDeployed,
  isMultiVersion,
} from '@src/emp-3.0/utils/checks';
import {
  renderDeployedStatusDeletePrompt,
  renderHasRelatedDeletePrompt,
  renderRelatedList,
} from '@src/emp-3.0/components/Management/components/CellRenderers/ActionConfirmButton';
import { DuplicatePopupContent } from '@src/emp-3.0/components/MappedEventBuilder/DuplicatePopupContent.tsx';
const unblockConfirmTitle = (
  <div
    style={{
      width: '404px',
      fontWeight: 500,
      letterSpacing: '-0.14px',
      lineHeight: '20px',
      color: '#333',
    }}
  >
    Unblock this item? Once unblocked, it will be removed from the Blocked Events and sensor data
    reporting will resume.
  </div>
);

const getDeleteConfirmTitle = (hasRelatedMetrics?: boolean, data?: MappedEvent) => {
  if (hasRelatedMetrics) {
    return (
      <div>
        {renderHasRelatedDeletePrompt()}
        {renderRelatedList(data)}
      </div>
    );
  }
  return <div style={{ whiteSpace: 'nowrap' }}>{renderDeployedStatusDeletePrompt()}</div>;
};

const popconfirmOkButtonStyle = {
  color: '#ff4d4f',
  borderRadius: '4px',
  border: '1px solid #FF4D4F',
  padding: '4px 8px',
  display: 'inline-flex',
  alignItems: 'center',
  fontWeight: 500,
  lineHeight: '20px',
  letterSpacing: '-0.14px',
  height: '28px',
};

const popconfirmCancelButtonStyle = {
  ...popconfirmOkButtonStyle,
  color: '#494949',
  borderColor: '#686868',
};

const ToolBar: React.FC<IToolBar> = ({
  loading,
  type = EEventManagementTabsType.MAPPED_EVENT,
  mode,
  data,
  duplicateMappedEventName,
  setDuplicateMappedEventName,
  onEdit,
  onDelete,
  onUnblock,
  onClose,
  onSave,
  onCancel,
  onClone,
  onSaveAs,
}) => {
  const isMappedEventData = data && 'related_metrics' in data && 'rules' in data;
  const isHasRelatedMetrics = hasRelatedMetrics(data);
  const isHasRelatedFlows = hasRelatedFlows(data);
  const isHasRelated = isHasRelatedMetrics || isHasRelatedFlows;
  const isSubVersion = isMultiVersion(data) && data?.is_draft === EDraftStatus.isDraft;
  const isTraceId =
    data && 'dimension_identifier' in data && data?.dimension_identifier === 'trace_id';
  const isDeploy = isDeployed(data);

  const enableEdit = !isMultiVersion(data) || isSubVersion;
  const canDelete = !isHasRelated; // Currently only used for metric and mapped event

  const hasCreate = useCreatePermissions();

  const isAllMetadataRulesEmpty = useMemo(() => {
    const rules = (isMappedEventData && data?.rules?.[0]) || [];

    if (rules.length > 0) {
      return rules.some((event) => {
        return (
          event?.metadata &&
          event.metadata.length > 0 &&
          event.metadata.some((andRules) => {
            return andRules?.every((rule) => rule?.key && rule?.value?.length === 0);
          })
        );
      });
    }

    return false;
  }, [data, isMappedEventData]);
  const duplicatePopupContent = (
    <DuplicatePopupContent
      duplicateMappedEventName={duplicateMappedEventName}
      footer={
        <>
          <Button
            onClick={() => {
              setDuplicateMappedEventName('');
            }}
          >
            Edit This Rule
          </Button>
        </>
      }
    ></DuplicatePopupContent>
  );

  const ReadOnlyToolBar = () => (
    <ToolBarContainer>
      <ToolBarLeft></ToolBarLeft>
      <ToolBarRight>
        <ButtonKit
          kitType="hover_primary"
          children="Close"
          prefix={<Icon component={CloseBlueIcon} />}
          onClick={onClose}
        />
      </ToolBarRight>
    </ToolBarContainer>
  );

  const ViewToolBar = () => {
    const RenderConfirmButton = () => {
      if (
        isMappedEventData &&
        (type === EEventManagementTabsType.METRICS ||
          (type === EEventManagementTabsType.MAPPED_EVENT && !isSubVersion))
      ) {
        return hasCreate ? (
          <Popconfirm
            placement="topRight"
            key="Delete Mapping Rules"
            title={getDeleteConfirmTitle(isHasRelated, data)}
            showCancel={canDelete}
            okText={canDelete ? (isDeploy ? 'Undeploy & Delete' : 'Delete') : 'Got it'}
            cancelButtonProps={{ type: 'default' }}
            onConfirm={() => {
              if (canDelete) {
                onDelete?.();
              }
            }}
            okButtonProps={{
              danger: canDelete,
              type: 'ghost',
              style: canDelete
                ? {
                    border: '1px solid #d0021b',
                    color: '#d0021b',
                  }
                : {
                    border: '1px solid #0046ff',
                    color: '#0046ff',
                  },
            }}
            icon={false}
          >
            <ButtonKit
              loading={loading}
              kitType="hover_primary"
              children="Delete"
              prefix={<Icon component={DeleteBlueIcon} />}
            />
          </Popconfirm>
        ) : null;
      }
      // else if (type === EEventManagementTabsType.BLOCKED_EVENT) {
      //   return hasCreate ? (
      //     <Popconfirm
      //       title={unblockConfirmTitle}
      //       onConfirm={onUnblock}
      //       okText="Unblock It"
      //       cancelButtonProps={{ type: 'default', style: popconfirmCancelButtonStyle }}
      //       okButtonProps={{
      //         danger: true,
      //         type: 'ghost',
      //         style: popconfirmOkButtonStyle,
      //       }}
      //       icon=""
      //       placement="bottomLeft"
      //     >
      //       <ButtonKit
      //         loading={loading}
      //         kitType="hover_primary"
      //         children="Unblock"
      //         prefix={<Icon component={UnblockIcon} />}
      //       />
      //     </Popconfirm>
      //   ) : null;
      // }
      else if (type === EEventManagementTabsType.CUSTOM_DIMENSION && !isSubVersion && !isTraceId) {
        return hasCreate ? (
          <Popconfirm
            placement="topRight"
            title={renderDeployedStatusDeletePrompt()}
            okText={isDeploy ? 'Undeploy & Delete' : 'Delete'}
            onConfirm={onDelete}
            cancelButtonProps={{ type: 'default' }}
            okButtonProps={{
              danger: true,
              type: 'ghost',
              style: {
                border: '1px solid #d0021b',
                color: '#d0021b',
              },
            }}
            icon={false}
          >
            <ButtonKit
              loading={loading}
              kitType="hover_primary"
              children="Delete"
              prefix={<Icon component={DeleteBlueIcon} />}
            />
          </Popconfirm>
        ) : null;
      }
      return null;
    };

    return (
      <ToolBarContainer>
        <ToolBarLeft>
          {hasCreate ? (
            <ButtonKit
              onClick={onEdit}
              loading={loading}
              kitType="hover_primary"
              children="Edit"
              prefix={<Icon component={EditIcon} />}
            />
          ) : null}

          {(type === EEventManagementTabsType.MAPPED_EVENT ||
            type === EEventManagementTabsType.CUSTOM_DIMENSION) &&
            !isTraceId &&
            hasCreate && (
              <ButtonKit
                loading={loading}
                kitType="hover_primary"
                children="Clone"
                prefix={<Icon component={CloneIcon} />}
                onClick={onClone}
              />
            )}
          <RenderConfirmButton />
        </ToolBarLeft>
        <ToolBarRight>
          <ButtonKit
            kitType="hover_primary"
            children="Close"
            prefix={<Icon component={CloseBlueIcon} />}
            onClick={onClose}
          />
        </ToolBarRight>
      </ToolBarContainer>
    );
  };

  const EditToolBar = () => (
    <ToolBarContainer>
      <ToolBarLeft>
        <Popover
          content={duplicatePopupContent}
          open={!!duplicateMappedEventName}
          trigger={'click'}
          onOpenChange={(v) => {
            !v && setDuplicateMappedEventName('');
          }}
        >
          <ButtonKit
            kitType="primary_highlight"
            children="Save Changes"
            prefix={<Icon component={SaveIcon} />}
            style={{ gap: '6px' }}
            onClick={onSave}
            disabled={isAllMetadataRulesEmpty}
          />
        </Popover>
        <ButtonKit
          kitType="primary"
          children="Cancel"
          onClick={onCancel}
          style={{ borderRadius: '2px' }}
        />
      </ToolBarLeft>
      <ToolBarRight>
        {(type === EEventManagementTabsType.MAPPED_EVENT ||
          type === EEventManagementTabsType.METRICS) && (
          <StyledAntAlert
            message="Note: Editing an event may affect the related metrics."
            type="warning"
            showIcon
            icon={<Icon component={AlertWarningIcon} />}
          />
        )}
      </ToolBarRight>
    </ToolBarContainer>
  );
  const CloneToolBar = () => (
    <ToolBarContainer>
      <ToolBarLeft>
        <Popover
          content={duplicatePopupContent}
          open={!!duplicateMappedEventName}
          trigger={'click'}
          onOpenChange={(v) => {
            !v && setDuplicateMappedEventName('');
          }}
        >
          <ButtonKit
            kitType="primary_highlight"
            children="Save"
            prefix={<Icon component={SaveIcon} />}
            style={{ gap: '6px' }}
            onClick={onSaveAs}
            disabled={isAllMetadataRulesEmpty}
          />
        </Popover>
        <ButtonKit
          kitType="primary"
          children="Cancel"
          onClick={onCancel}
          style={{ borderRadius: '2px' }}
        />
      </ToolBarLeft>
      <ToolBarRight>
        <StatusTag
          size="large"
          type="light"
          typeColor="blue"
          style={{
            background: '#e9f3fd',
          }}
        >
          Cloning...
        </StatusTag>
      </ToolBarRight>
    </ToolBarContainer>
  );
  let toolBarComponent: JSX.Element;
  const sharedToolBar =
    mode === 'edit' ? <EditToolBar /> : mode === 'clone' ? <CloneToolBar /> : <ViewToolBar />;

  switch (type) {
    case EEventManagementTabsType.MAPPED_EVENT:
      toolBarComponent = enableEdit ? sharedToolBar : <ReadOnlyToolBar />;
      break;
    case EEventManagementTabsType.BLOCKED_EVENT:
      toolBarComponent = sharedToolBar;
      break;
    case EEventManagementTabsType.CUSTOM_DIMENSION:
      toolBarComponent = enableEdit ? sharedToolBar : <ReadOnlyToolBar />;
      break;
    default:
      toolBarComponent = <ReadOnlyToolBar />;
      break;
  }

  return <>{toolBarComponent}</>;
};

export default ToolBar;
